import { afterNextRender, AfterViewInit, Component, computed, ElementRef, Injector, OnDestroy, ViewChild, forwardRef, inject, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Link as TiptapLink } from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { TextAlign } from '@tiptap/extension-text-align';
import { BubbleMenu } from '@tiptap/extension-bubble-menu';
import { BaseInputComponentClass } from '../../core/base-classes/base-input-component-class';
import { DocumentFilePickerComponent } from '../document-file-picker/document-file-picker.component';
import { InputLabel } from '../input-label/input-label.component';
import { DocumentService } from '../../core/services/document.service';
import { DocumentNodeModel } from '../../core/models/data/document.model';
import { DocumentPermission, FileTypeCategory } from '../../core/enums/data.enums';
import { EmailSafeHighlight } from './extensions/rich-text-highlight.extension';
import { FontFamily } from './extensions/rich-text-font-family.extension';
import { FontSize } from './extensions/rich-text-font-size.extension';
import { ResizableImage, ResizableImageAlign } from './extensions/rich-text-resizable-image.extension';
import { RichTextClipboardUtilities } from './rich-text-clipboard.utilities';

@Component({
  selector: 'ms-rich-text-editor',
  standalone: true,
  imports: [FontAwesomeModule, DocumentFilePickerComponent, InputLabel],
  templateUrl: './rich-text-editor.component.html',
  styleUrl: './rich-text-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ]
})
export class RichTextEditorComponent extends BaseInputComponentClass implements AfterViewInit, OnDestroy, ControlValueAccessor {

  //#region Properties

  @ViewChild('editorHost', { static: true }) private editorHost!: ElementRef<HTMLElement>;
  @ViewChild('linkBubbleMenu', { static: true }) private linkBubbleMenu!: ElementRef<HTMLElement>;
  @ViewChild('linkEditInput') private linkEditInput?: ElementRef<HTMLInputElement>;
  @ViewChild('linkTextInput') private linkTextInput?: ElementRef<HTMLInputElement>;
  @ViewChild('imagePicker') private imagePicker?: DocumentFilePickerComponent;

  // Hides the Insert Image toolbar button (and its image-wrap dropdown) when false. Kept true by default
  // so existing usages retain the picker; callers that must ship an email whose body cannot embed images
  // (e.g. the Support Ticket form) opt out by setting this to false.
  public allowImageInsert = input<boolean>(true);

  public readonly imagePickerModalId = `imagePickerModal-${Math.random().toString(36).slice(2, 8)}`;
  // Images embedded in an email body must be reachable by every recipient without auth — restrict the picker
  // to Public documents so the CDN URL we insert into <img src> works for everyone on the send list.
  public readonly imagePickerPermissions: DocumentPermission[] = [DocumentPermission.Public];
  public readonly imagePickerFileTypes: FileTypeCategory[] = [FileTypeCategory.Image];

  private documentService = inject(DocumentService);
  private bubbleInjector = inject(Injector);
  private editor?: Editor;
  // Holds the content set via writeValue before the editor is instantiated in ngAfterViewInit.
  private pendingValue: string | null = null;
  // Cached reference so ngOnDestroy can detach it — bound arrow keeps `this` and identity for removeEventListener.
  // Blocks the browser's default anchor navigation. Capture phase runs before the browser processes the
  // default action, so preventDefault here reliably stops <a target="_blank"> from opening a new tab. We
  // intentionally do NOT stopPropagation — ProseMirror needs to see the click to place the caret inside
  // the link. Auxclick covers middle-click, which bypasses regular click handlers.
  private readonly anchorClickBlocker = (event: Event): void => {
    const target = event.target;
    if (target instanceof Element && target.closest('a')) {
      event.preventDefault();
    }
  };
  private readonly anchorClickEventTypes = ['click', 'auxclick'] as const;

  // Link bubble state
  public bubbleLinkHref = signal<string>('');
  public isEditingLink = signal<boolean>(false);
  public linkEditValue = signal<string>('');
  public linkTextValue = signal<string>('');
  // Set true when the user clicks the close (X) button on the display bubble. Suppresses shouldShow until
  // the caret leaves the link mark, at which point refreshActiveState resets it so re-entering the link
  // reveals the bubble again.
  private bubbleDismissed = signal<boolean>(false);
  // Both fields are required for text-selection edits. When an image is the selection, the link wraps the
  // image itself (no visible text to configure), so the Text input is hidden and only URL is required.
  // URL is trimmed (whitespace around a URL is always a mistake); Text is NOT trimmed — the user may
  // deliberately want to link a run of whitespace (e.g. a space between two words), so length > 0 is enough.
  public isLinkEditValid = computed(() => {
    if (this.linkEditValue().trim().length === 0) { return false; }
    return this.isImageSelected() || this.linkTextValue().length > 0;
  });

  public isBoldActive = signal(false);
  public isItalicActive = signal(false);
  public isStrikeActive = signal(false);
  public isUnderlineActive = signal(false);
  public isHeading1Active = signal(false);
  public isHeading2Active = signal(false);
  public isHeading3Active = signal(false);
  public isHeading4Active = signal(false);
  public isHeading5Active = signal(false);
  public isHeading6Active = signal(false);
  public isBulletListActive = signal(false);
  public isOrderedListActive = signal(false);
  public isBlockquoteActive = signal(false);
  public isLinkActive = signal(false);
  public currentColor = signal<string | null>(null);
  public currentFontSize = signal<string | null>(null);
  public currentFontFamily = signal<string | null>(null);
  public currentAlignment = signal<'left' | 'center' | 'right' | 'justify'>('left');
  public isHighlightActive = signal(false);
  public currentHighlight = signal<string | null>(null);
  public isImageSelected = signal(false);
  public currentImageAlign = signal<ResizableImageAlign | null>(null);

  public alignmentIcon = computed(() => {
    const match = this.alignmentOptions.find(o => o.value === this.currentAlignment());
    return match?.icon ?? 'align-left';
  });

  public imageWrapIcon = computed(() => {
    const match = this.imageWrapOptions.find(o => o.value === (this.currentImageAlign() ?? ''));
    return match?.icon ?? 'image';
  });

  public isAnyHeadingActive = computed(() =>
    this.isHeading1Active() || this.isHeading2Active() || this.isHeading3Active()
    || this.isHeading4Active() || this.isHeading5Active() || this.isHeading6Active()
  );

  //#endregion

  //#region Toolbar Options

  public readonly alignmentOptions: readonly { name: string; value: 'left' | 'center' | 'right' | 'justify'; icon: string }[] = [
    { name: 'Left', value: 'left', icon: 'align-left' },
    { name: 'Center', value: 'center', icon: 'align-center' },
    { name: 'Right', value: 'right', icon: 'align-right' },
    { name: 'Justify', value: 'justify', icon: 'align-justify' }
  ];

  // Wrap dropdown only affects the currently selected image node. Empty value clears the wrap (image reverts
  // to a plain inline node in its paragraph); left/right float the image so paragraph text flows around it.
  // Center isn't offered because CSS float has no `center` value — users centre an image by centring its
  // containing paragraph via the alignment buttons.
  public readonly imageWrapOptions: readonly { name: string; value: '' | ResizableImageAlign; icon: string }[] = [
    { name: 'No wrap', value: '', icon: 'image' },
    { name: 'Wrap left', value: 'left', icon: 'objects-align-left' },
    { name: 'Wrap right', value: 'right', icon: 'objects-align-right' }
  ];

  public readonly highlightPalette: readonly { name: string; value: string }[] = [
    { name: 'None', value: '' },
    { name: 'Yellow', value: '#fff3b0' },
    { name: 'Orange', value: '#ffd8a8' },
    { name: 'Red', value: '#ffc9c9' },
    { name: 'Pink', value: '#fcc2d7' },
    { name: 'Purple', value: '#e5dbff' },
    { name: 'Blue', value: '#c5dafd' },
    { name: 'Teal', value: '#c3fae8' },
    { name: 'Green', value: '#d3f9d8' },
    { name: 'Grey', value: '#e9ecef' }
  ];

  // Email-safe font stacks — restrict to fonts installed on the majority of email-client platforms so
  // recipients see the intended typeface instead of the client's fallback.
  public readonly fontFamilyOptions: readonly { name: string; value: string }[] = [
    { name: 'Default', value: '' },
    { name: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { name: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { name: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
    { name: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
    { name: 'Times New Roman', value: 'Times New Roman, Times, serif' },
    { name: 'Georgia', value: 'Georgia, serif' },
    { name: 'Courier New', value: 'Courier New, Courier, monospace' }
  ];

  public readonly fontSizeOptions: readonly { name: string; value: string }[] = [
    { name: 'Default', value: '' },
    { name: 'Small', value: '0.75rem' },
    { name: 'Normal', value: '1rem' },
    { name: 'Medium', value: '1.25rem' },
    { name: 'Large', value: '1.5rem' },
    { name: 'Extra Large', value: '2rem' },
    { name: 'Huge', value: '3rem' }
  ];

  public readonly colorPalette: readonly { name: string; value: string }[] = [
    { name: 'Default', value: '' },
    { name: 'Black', value: '#000000' },
    { name: 'Grey', value: '#6c757d' },
    { name: 'Red', value: '#dc3545' },
    { name: 'Orange', value: '#fd7e14' },
    { name: 'Yellow', value: '#ffc107' },
    { name: 'Green', value: '#198754' },
    { name: 'Teal', value: '#20c997' },
    { name: 'Blue', value: '#0d6efd' },
    { name: 'Indigo', value: '#6610f2' },
    { name: 'Purple', value: '#6f42c1' },
    { name: 'Pink', value: '#d63384' }
  ];

  //#endregion

  //#region Lifecycle

  public ngAfterViewInit(): void {
    this.editor = new Editor({
      element: this.editorHost.nativeElement,
      editorProps: {
        transformPastedHTML: html => RichTextClipboardUtilities.normalizePastedHtml(html),
        transformPasted: slice => RichTextClipboardUtilities.openPastedBlockSlice(slice)
      },
      extensions: [
        // Inline styles on paragraph + blockquote ride along via StarterKit's HTMLAttributes forwarding so
        // they ship in the rendered email, not just the editor preview (email clients ignore external CSS).
        StarterKit.configure({
          paragraph: {
            HTMLAttributes: {
              style: 'margin: 0 0 1rem; line-height: 1.5'
            }
          },
          blockquote: {
            HTMLAttributes: {
              style: 'border-left: 3px solid #adb5bd; padding-left: 1rem; margin: 1rem 0; color: #6c757d'
            }
          }
        }),
        // Inline mode lets the image live inside its containing paragraph, so paragraph text-align aligns
        // the image and users can select the image and use the existing alignment toolbar buttons.
        ResizableImage.configure({ inline: true }),
        // Tiptap's Link extension ties `inclusive` to `autolink`, so enabling autolink also makes the link
        // mark "sticky" — typing at the boundary of a link (or right after a linked image) extends the mark
        // to the new text. We want autolink but not stickiness, so override inclusive to false directly.
        TiptapLink.extend({ inclusive: () => false })
          .configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
        TextStyle,
        Color,
        FontFamily,
        FontSize,
        TextAlign.configure({ types: ['heading', 'paragraph'], defaultAlignment: 'left' }),
        EmailSafeHighlight.configure({ multicolor: true }),
        BubbleMenu.configure({
          element: this.linkBubbleMenu.nativeElement,
          pluginKey: 'linkBubbleMenu',
          shouldShow: ({ editor }) => (editor.isActive('link') && !this.bubbleDismissed()) || this.isEditingLink(),
          options: { placement: 'bottom', offset: 8 }
        })
      ],
      content: this.pendingValue ?? '',
      onUpdate: ({ editor }) => {
        // An empty ProseMirror doc still stringifies to "<p></p>", which slips past Validators.required.
        // Emit an actual empty string when there is no visible text so required validation behaves.
        const html = editor.isEmpty ? '' : editor.getHTML();
        this.onChange(html);
        this.refreshActiveState();
      },
      onSelectionUpdate: () => this.refreshActiveState(),
      onBlur: () => this.onBlur()
    });
    this.pendingValue = null;
    // Capture-phase listeners — run before the browser can act on an anchor event, so `target="_blank"`
    // links inside the editor no longer open a new tab when clicked (Tiptap's `openOnClick: false` only
    // blocks Tiptap's own handler, not the browser default). Removed in ngOnDestroy.
    for (const type of this.anchorClickEventTypes) {
      this.editorHost.nativeElement.addEventListener(type, this.anchorClickBlocker, { capture: true });
    }
  }

  public ngOnDestroy(): void {
    if (this.editorHost) {
      for (const type of this.anchorClickEventTypes) {
        this.editorHost.nativeElement.removeEventListener(type, this.anchorClickBlocker, { capture: true } as EventListenerOptions);
      }
    }
    this.editor?.destroy();
  }

  //#endregion

  //#region ControlValueAccessor

  public writeValue(value: string | null): void {
    const html = value ?? '';
    if (!this.editor) {
      this.pendingValue = html;
      return;
    }
    // setContent('') produces a doc with no children, leaving the ProseMirror view empty and non-interactive
    // after a form reset. clearContent() resets to a single empty paragraph so the editor stays usable.
    if (html) {
      this.editor.commands.setContent(html, { emitUpdate: false });
    } else {
      this.editor.commands.clearContent(false);
    }
    this.refreshActiveState();
  }

  //#endregion

  //#region Toolbar Commands

  public toggleBold(): void { this.editor?.chain().focus().toggleBold().run(); }
  public toggleItalic(): void { this.editor?.chain().focus().toggleItalic().run(); }
  public toggleStrike(): void { this.editor?.chain().focus().toggleStrike().run(); }
  public toggleUnderline(): void { this.editor?.chain().focus().toggleUnderline().run(); }
  public toggleHeading(level: 1 | 2 | 3 | 4 | 5 | 6): void {
    this.editor?.chain().focus().toggleHeading({ level }).run();
  }
  public toggleBulletList(): void { this.editor?.chain().focus().toggleBulletList().run(); }
  public toggleOrderedList(): void { this.editor?.chain().focus().toggleOrderedList().run(); }
  public toggleBlockquote(): void { this.editor?.chain().focus().toggleBlockquote().run(); }
  public setHorizontalRule(): void { this.editor?.chain().focus().setHorizontalRule().run(); }
  public setHardBreak(): void { this.editor?.chain().focus().setHardBreak().run(); }
  public clearFormatting(): void { this.editor?.chain().focus().unsetAllMarks().clearNodes().run(); }
  public undo(): void { this.editor?.chain().focus().undo().run(); }
  public redo(): void { this.editor?.chain().focus().redo().run(); }
  public selectAll(): void { this.editor?.chain().focus().selectAll().run(); }

  public setColor(color: string): void {
    if (color) {
      this.editor?.chain().focus().setColor(color).run();
    } else {
      this.editor?.chain().focus().unsetColor().run();
    }
  }

  public onCustomColorInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      this.setColor(value);
    }
  }

  public setFontSize(fontSize: string): void {
    const chain = this.editor?.chain().focus();
    if (!chain) { return; }
    if (fontSize) {
      chain.setFontSize(fontSize).run();
    } else {
      chain.unsetFontSize().run();
    }
  }

  public setFontFamily(fontFamily: string): void {
    const chain = this.editor?.chain().focus();
    if (!chain) { return; }
    if (fontFamily) {
      chain.setFontFamily(fontFamily).run();
    } else {
      chain.unsetFontFamily().run();
    }
  }

  public setAlignment(alignment: 'left' | 'center' | 'right' | 'justify'): void {
    this.editor?.chain().focus().setTextAlign(alignment).run();
  }

  // Empty string clears the wrap so the image goes back to plain inline flow in its paragraph.
  public setImageAlign(align: '' | ResizableImageAlign): void {
    this.editor?.chain().focus().updateAttributes('image', { align: align || null }).run();
  }

  public setHighlight(color: string): void {
    if (color) {
      this.editor?.chain().focus().setHighlight({ color }).run();
    } else {
      this.editor?.chain().focus().unsetHighlight().run();
    }
  }

  public onCustomHighlightInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      this.setHighlight(value);
    }
  }

  public openImagePicker(): void {
    void this.imagePicker?.open();
  }

  // The picker emits a DocumentNodeModel; we resolve its CDN URL and insert an <img> at the current selection.
  // Public files return a stable download URL that any recipient can load. If resolution fails, silently skip
  // the insert — surfacing an error mid-edit is more disruptive than the user retrying the pick.
  public async onImageFileSelected(file: DocumentNodeModel): Promise<void> {
    const result = await this.documentService.getDownloadUrl(file.documentNodeId);
    const src = result.data?.downloadUrl;
    if (!src) { return; }
    this.editor?.chain().focus().setImage({ src }).run();
  }

  // Puts the bubble menu into edit mode. For text edits, if the caret is on an existing link we extend the
  // selection to that link's full range so the text field gets the whole link's text (not just the word the
  // caret was inside) and applyLinkEdit rewrites the whole thing. For image edits, the NodeSelection over
  // the image already covers exactly the anchor's contents — extending would change it to a text range.
  public startLinkEdit(): void {
    if (!this.editor) { return; }
    const editingImage = this.isImageSelected();
    if (!editingImage && this.editor.isActive('link')) {
      this.editor.chain().focus().extendMarkRange('link').run();
    } else if (!this.editor.isFocused) {
      // Cold click from the toolbar with no prior editor focus — restore focus against the editor's
      // current selection so the BubbleMenu has something to anchor against and applyLinkEdit's
      // insertContentAt has a concrete position to insert into. Passing no position preserves whatever
      // selection ProseMirror already has (so a programmatic selection isn't collapsed).
      this.editor.commands.focus();
    }
    const existingHref = (this.editor.getAttributes('link')?.['href'] as string | undefined) ?? '';
    let selectedText = '';
    if (!editingImage) {
      const { from, to, empty } = this.editor.state.selection;
      // textBetween with a single-space separator flattens hard breaks/inline nodes to a printable string.
      selectedText = empty ? '' : this.editor.state.doc.textBetween(from, to, ' ');
    }
    this.linkEditValue.set(existingHref);
    this.linkTextValue.set(selectedText);
    this.isEditingLink.set(true);
    // BubbleMenu's shouldShow only re-runs when the selection or doc actually changes. Clicking the toolbar
    // does neither (the button's mousedown-preventDefault keeps focus on the editor), so we drive the plugin
    // directly via its own metas: 'show' flips visibility, then 'updatePosition' anchors it against the
    // current selection. updatePosition alone is a no-op while the bubble is hidden, so order matters.
    this.editor.view.dispatch(this.editor.state.tr.setMeta('linkBubbleMenu', 'show'));
    this.editor.view.dispatch(this.editor.state.tr.setMeta('linkBubbleMenu', 'updatePosition'));
    // afterNextRender runs after Angular renders the @if (isEditingLink()) branch, so the input view
    // children exist by the time we focus. Focus Text when it is rendered AND empty (a brand-new link from
    // a caret / new URL with no prefilled selection); otherwise focus URL (the missing piece when Text was
    // prefilled from the selection, or the only field for image links). The plugin's blur handler ignores
    // blurs whose relatedTarget is inside the bubble element, so this doesn't hide the bubble.
    afterNextRender(() => {
      const shouldFocusText = !this.isImageSelected() && this.linkTextValue().length === 0;
      const input = shouldFocusText ? this.linkTextInput : this.linkEditInput;
      input?.nativeElement.focus();
    }, { injector: this.bubbleInjector });
  }

  public applyLinkEdit(): void {
    if (!this.editor) { return; }
    const href = this.linkEditValue().trim();
    const editingImage = this.isImageSelected();
    // Text is NOT trimmed — a whitespace-only Text is a legitimate link (matches the toolbar-enable rule).
    const text = this.linkTextValue();
    // Defensive — the Apply button is disabled via isLinkEditValid when required fields are blank, but bail
    // rather than write an invalid link if we're somehow called anyway. Image links only require URL.
    if (!href || (!editingImage && text.length === 0)) { return; }

    if (editingImage) {
      // Apply the link mark directly to the image NodeSelection. Tiptap's setLink → setMark → addMark adds
      // the mark to the range covering the image node, and on serialize the link mark wraps the <img> in an
      // <a href="…">, which every major email client renders.
      this.editor.chain().focus().setLink({ href }).run();
    } else {
      const { from, to, empty } = this.editor.state.selection;
      const currentText = empty ? '' : this.editor.state.doc.textBetween(from, to, ' ');
      if (!empty && currentText === text) {
        // Text unchanged — only rewrite the href so any inner marks (bold, italic, colour, font, …) survive.
        // insertContentAt would flatten the range to a single text node carrying only the link mark.
        this.editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
      } else {
        // Text changed (or new insert at a caret) — replace the range with a fresh text node carrying just
        // the link mark. Other marks are legitimately gone because the source characters are gone.
        this.editor.chain().focus()
          .insertContentAt({ from, to }, { type: 'text', text, marks: [{ type: 'link', attrs: { href } }] })
          .run();
      }
    }
    this.cancelLinkEdit();
  }

  public cancelLinkEdit(): void {
    this.isEditingLink.set(false);
    this.linkEditValue.set('');
    this.linkTextValue.set('');
    // Refocus the editor so the plugin re-runs shouldShow and hides the bubble (a no-op dispatch is skipped
    // by the plugin, so focus is the reliable trigger).
    this.editor?.commands.focus();
  }

  public onLinkEditInput(event: Event): void {
    this.linkEditValue.set((event.target as HTMLInputElement).value);
  }

  public onLinkTextInput(event: Event): void {
    this.linkTextValue.set((event.target as HTMLInputElement).value);
  }

  public onLinkEditKeydown(event: KeyboardEvent): void {
    // Prevent any editor-level or document-level keymap from ever seeing keystrokes typed into the link
    // inputs (bound by both the Text and URL fields in edit mode).
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      this.applyLinkEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelLinkEdit();
    }
  }

  // Auto-cancel edit mode when focus leaves the bubble entirely (click in editor, click outside, tab away).
  // Blurs whose relatedTarget is another element inside the bubble (Apply / Cancel button, still-focused input)
  // are ignored so the bubble stays open long enough for those clicks to fire.
  public onLinkEditBlur(event: FocusEvent): void {
    const nextFocus = event.relatedTarget as HTMLElement | null;
    if (nextFocus && this.linkBubbleMenu.nativeElement.contains(nextFocus)) {
      return;
    }
    this.cancelLinkEdit();
  }

  // Hides the display-mode bubble until the user's caret leaves the link mark. Dispatch 'hide' so the
  // plugin removes the element immediately — shouldShow will already be returning false because of the
  // bubbleDismissed flag we just set, so the plugin won't re-show it on the next update.
  public closeBubble(): void {
    this.bubbleDismissed.set(true);
    this.editor?.view.dispatch(this.editor.state.tr.setMeta('linkBubbleMenu', 'hide'));
  }

  public openBubbleLink(): void {
    const href = this.bubbleLinkHref();
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }

  public removeBubbleLink(): void {
    this.editor?.chain().focus().extendMarkRange('link').unsetLink().run();
  }

  //#endregion

  //#region Internal

  private refreshActiveState(): void {
    if (!this.editor) {
      return;
    }
    this.isBoldActive.set(this.editor.isActive('bold'));
    this.isItalicActive.set(this.editor.isActive('italic'));
    this.isStrikeActive.set(this.editor.isActive('strike'));
    this.isUnderlineActive.set(this.editor.isActive('underline'));
    this.isHeading1Active.set(this.editor.isActive('heading', { level: 1 }));
    this.isHeading2Active.set(this.editor.isActive('heading', { level: 2 }));
    this.isHeading3Active.set(this.editor.isActive('heading', { level: 3 }));
    this.isHeading4Active.set(this.editor.isActive('heading', { level: 4 }));
    this.isHeading5Active.set(this.editor.isActive('heading', { level: 5 }));
    this.isHeading6Active.set(this.editor.isActive('heading', { level: 6 }));
    this.isBulletListActive.set(this.editor.isActive('bulletList'));
    this.isOrderedListActive.set(this.editor.isActive('orderedList'));
    this.isBlockquoteActive.set(this.editor.isActive('blockquote'));
    const isLink = this.editor.isActive('link');
    this.isLinkActive.set(isLink);
    this.bubbleLinkHref.set(isLink ? (this.editor.getAttributes('link')?.['href'] as string ?? '') : '');
    // Reset the dismissed flag once the caret is no longer on any link — the next time it enters a link
    // (same one or another), the bubble should surface again.
    if (!isLink) {
      this.bubbleDismissed.set(false);
    }
    const textStyleAttrs = this.editor.getAttributes('textStyle');
    this.currentColor.set((textStyleAttrs?.['color'] as string | undefined) ?? null);
    this.currentFontSize.set((textStyleAttrs?.['fontSize'] as string | undefined) ?? null);
    this.currentFontFamily.set((textStyleAttrs?.['fontFamily'] as string | undefined) ?? null);
    const alignment = this.alignmentOptions.map(o => o.value).find(v => this.editor!.isActive({ textAlign: v }));
    this.currentAlignment.set(alignment ?? 'left');
    this.isHighlightActive.set(this.editor.isActive('highlight'));
    const highlightColor = this.editor.getAttributes('highlight')?.['color'] as string | undefined;
    this.currentHighlight.set(highlightColor ?? null);
    const imageSelected = this.editor.isActive('image');
    this.isImageSelected.set(imageSelected);
    const imageAlign = imageSelected
      ? (this.editor.getAttributes('image')?.['align'] as ResizableImageAlign | undefined) ?? null
      : null;
    this.currentImageAlign.set(imageAlign);
  }

  //#endregion
}
